import { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Button } from '@/components/ui/button';
import { Download, ZoomIn, ZoomOut } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FileViewer, getFileCategory } from '@/components/ui/file-viewer';
import { nativeAuthedDownload } from './native-download';

export interface PreviewAttachment {
  id: string;
  filename: string;
  contentType: string;
  url?: string;
}

interface AttachmentPreviewModalProps {
  attachment: PreviewAttachment | null;
  onClose: () => void;
}

function useAuthenticatedBlobUrl(attachmentId: string | null) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!attachmentId) {
      setBlobUrl(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    let currentBlobUrl: string | null = null;

    async function fetchBlob() {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(`/api/chat/attachments/${attachmentId}`, {
          credentials: 'include',
          redirect: 'follow'
        });

        if (!response.ok) {
          throw new Error('Failed to load file');
        }

        const blob = await response.blob();
        if (!cancelled) {
          currentBlobUrl = URL.createObjectURL(blob);
          setBlobUrl(currentBlobUrl);
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load');
          setIsLoading(false);
        }
      }
    }

    fetchBlob();

    return () => {
      cancelled = true;
      if (currentBlobUrl) {
        URL.revokeObjectURL(currentBlobUrl);
      }
    };
  }, [attachmentId]);

  return { blobUrl, isLoading, error };
}

function computeSmartImageSize(
  naturalWidth: number,
  naturalHeight: number
): { width: number; height: number } {
  const retinaScale = 2;
  let displayW = naturalWidth * retinaScale;
  let displayH = naturalHeight * retinaScale;

  const maxW = window.innerWidth * 0.9;
  const maxH = window.innerHeight * 0.85;

  const minW = 200;
  const minH = 150;

  if (displayW > maxW || displayH > maxH) {
    const scaleW = maxW / displayW;
    const scaleH = maxH / displayH;
    const scale = Math.min(scaleW, scaleH);
    displayW = Math.round(displayW * scale);
    displayH = Math.round(displayH * scale);
  }

  displayW = Math.max(displayW, minW);
  displayH = Math.max(displayH, minH);

  return { width: displayW, height: displayH };
}

interface ZoomViewProps {
  blobUrl: string;
  containerWidth: number;
  containerHeight: number;
}

function ZoomView({ blobUrl, containerWidth, containerHeight }: ZoomViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [bgPos, setBgPos] = useState('center center');

  const zoomFactor = 3;

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setBgPos(`${x}% ${y}%`);
  }, []);

  return (
    <div
      ref={containerRef}
      className="cursor-crosshair"
      style={{
        width: containerWidth,
        height: containerHeight,
        backgroundImage: `url(${blobUrl})`,
        backgroundSize: `${zoomFactor * 100}%`,
        backgroundPosition: bgPos,
        backgroundRepeat: 'no-repeat',
      }}
      onMouseMove={handleMouseMove}
    />
  );
}

export function AttachmentPreviewModal({ attachment, onClose }: AttachmentPreviewModalProps) {
  const { blobUrl, isLoading, error } = useAuthenticatedBlobUrl(attachment?.id ?? null);
  const isOpen = attachment !== null;
  const fileCategory = attachment ? getFileCategory(attachment.contentType) : 'other';
  const isImage = fileCategory === 'image';

  const [imageDims, setImageDims] = useState<{ width: number; height: number } | null>(null);
  const [zoomActive, setZoomActive] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setImageDims(null);
      setZoomActive(false);
    }
  }, [isOpen]);

  const handleImageLoad = useCallback((naturalWidth: number, naturalHeight: number) => {
    const dims = computeSmartImageSize(naturalWidth, naturalHeight);
    setImageDims(dims);
  }, []);

  const handleDownload = useCallback(() => {
    if (!attachment) return;
    // Browser-native download — see native-download.ts for the rationale.
    void nativeAuthedDownload(`/api/chat/attachments/${attachment.id}?download=true`, attachment.filename);
  }, [attachment]);

  const dialogWidthClass = isImage && imageDims
    ? '!max-w-none !w-auto'
    : fileCategory === 'pdf'
      ? 'max-w-4xl w-[90vw]'
      : 'max-w-fit w-auto';

  const imageStyle: React.CSSProperties | undefined = isImage && imageDims
    ? { width: imageDims.width, height: imageDims.height, maxWidth: '100%' }
    : isImage
      ? { maxWidth: '100%', maxHeight: '80vh' }
      : undefined;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className={cn(
          "p-0 gap-0 border-gray-700 bg-gray-900",
          dialogWidthClass
        )}
        style={isImage && imageDims ? {
          width: imageDims.width + 32,
          maxWidth: '92vw',
        } : undefined}
      >
        <VisuallyHidden>
          <DialogTitle>{attachment?.filename || 'File Preview'}</DialogTitle>
        </VisuallyHidden>
        <div className="flex items-center justify-between pl-4 pr-12 py-2 border-b border-gray-700">
          <span className="text-sm font-medium text-white truncate max-w-[300px]">
            {attachment?.filename || 'Preview'}
          </span>
          <div className="flex items-center gap-1">
            {isImage && imageDims && blobUrl && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setZoomActive(!zoomActive)}
                className={cn(
                  "h-8 w-8",
                  zoomActive
                    ? "text-blue-400 hover:text-blue-300 bg-blue-500/10"
                    : "text-gray-400 hover:text-white"
                )}
                title={zoomActive ? 'Exit zoom (3x)' : 'Zoom (3x magnification)'}
              >
                {zoomActive ? <ZoomOut className="h-4 w-4" /> : <ZoomIn className="h-4 w-4" />}
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDownload}
              className="h-8 w-8 text-gray-400 hover:text-white"
              title="Download"
            >
              <Download className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="p-4 bg-gray-950">
          {zoomActive && blobUrl && imageDims ? (
            <ZoomView
              blobUrl={blobUrl}
              containerWidth={imageDims.width}
              containerHeight={imageDims.height}
            />
          ) : (
            <FileViewer
              blobUrl={blobUrl}
              isLoading={isLoading}
              error={error}
              filename={attachment?.filename || 'Preview'}
              contentType={attachment?.contentType || 'application/octet-stream'}
              onDownload={handleDownload}
              onImageLoad={handleImageLoad}
              imageStyle={imageStyle}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
